import { useState, useEffect, useRef } from 'react';
import _find from 'lodash.find';
import { OverlayPanel } from 'primereact/overlaypanel';
import { getFormattedNumberStr } from '../../utils/number';
import {
  getReasonStrById,
  EditableValue,
  ValueWrapper,
  ValueUpdateInfo,
  SummaryWrapper,
  SummaryLabel,
  StaticValue,
} from './common';
import { useReportDetailStore } from '.';
import { DateTime } from 'luxon';

function MillSummary({
  data,
  updatedCells,
  reasonsData,
  onUpdate,
  isWater,
}: {
  data: any;
  updatedCells: any[];
  reasonsData: any[];
  onUpdate: any;
  isWater: boolean;
}) {
  const [item, setItem] = useState<any>(null);
  const [isUpdated, setUpdated] = useState<boolean>(false);
  const { userPermissions } = useReportDetailStore();
  const op = useRef<OverlayPanel>(null);

  const getDefaultItemData = () => ({
    metricId: data.metricId,
    value: data.value,
    reasonId: data.reasonId,
    metricUnit: data.metricUnit,
    wpsProduction: data.wpsProduction,
    wpsProductionUnit: data.wpsProductionUnit,
    wpsProductionFormula: data.wpsProductionFormula,
    oneStreamProductionValue: data.oneStreamProductionValue,
    oneStreamProductionUnit: data.oneStreamProductionUnit,
    formula: data.formula,
    updatedBy: data.createdBy
      ? {
          user: data.createdBy,
          reasonId: data.reasonId,
          createdAt: data.createdAt,
        }
      : null,
  });

  useEffect(() => {
    if (data) {
      setItem(getDefaultItemData());
    }
  }, [data]);

  useEffect(() => {
    const updatedValue = _find(updatedCells, (i: any) => i.metricId === item?.metricId);

    if (updatedValue) {
      setUpdated(true);
      setItem((prev: any) => ({
        ...prev,
        ...updatedValue,
      }));
    } else {
      setItem(getDefaultItemData());
      setUpdated(false);
    }
  }, [updatedCells]);

  if (!item) {
    return null;
  }

  return (
    <SummaryWrapper>
      <div>
        <SummaryLabel>Mill total:</SummaryLabel>
        <ValueWrapper>
          {userPermissions.canUpdateFigures ? (
            <EditableValue
              onClick={() =>
                onUpdate({
                  data: {
                    value: item.value,
                    reasonId: item.reasonId,
                    metricUnit: item.metricUnit,
                    metricId: item.metricId,
                  },
                })
              }
              isUpdated={isUpdated}
            >
              <i className="pi pi-pencil" />
              {getFormattedNumberStr(Math.round(item.value), item.metricUnit)}
            </EditableValue>
          ) : (
            <StaticValue>{getFormattedNumberStr(Math.round(item.value), item.metricUnit)}</StaticValue>
          )}

          {item.updatedBy && (
            <>
              <i className="cellChangeInfo pi pi-info-circle" onClick={(e) => op.current?.toggle(e)} />
              <OverlayPanel
                className="em-custom-overlaypanel"
                ref={op}
                style={{ fontSize: '12px', marginRight: '16px' }}
                showCloseIcon
              >
                <ValueUpdateInfo>
                  <p>Last value update:</p>
                  <span>User:</span>
                  <span>{item.updatedBy.user}</span>
                  <span>Reason:</span>
                  <span>{getReasonStrById(item.updatedBy.reasonId, reasonsData)}</span>
                  <span>Updated at:</span>
                  <span>
                    {item.updatedBy.createdAt
                      ? DateTime.fromISO(item.updatedBy.createdAt).toLocaleString({
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: 'numeric',
                          minute: 'numeric',
                          second: 'numeric',
                        })
                      : '-'}
                  </span>
                </ValueUpdateInfo>
              </OverlayPanel>
            </>
          )}
        </ValueWrapper>
      </div>
      {!isWater && 
        <div>
          <SummaryLabel>Formula:</SummaryLabel> <span>{item.formula}</span>
        </div>
      }
      <div>
        <SummaryLabel>One Stream Production:</SummaryLabel>{' '}
        <span>{getFormattedNumberStr(Math.round(item.oneStreamProductionValue), item.oneStreamProductionUnit)}</span>
      </div>
      <div>
        <SummaryLabel>WPS Production:</SummaryLabel>{' '}
        <span>{getFormattedNumberStr(Math.round(item.wpsProduction), item.wpsProductionUnit)}</span>
      </div>
      {!isWater &&
        <div style={{ gridColumn: 'span 2' }}>
          <SummaryLabel>WPS Production Formula:</SummaryLabel> <span>{item.wpsProductionFormula}</span>
        </div>
      }
    </SummaryWrapper>
  );
}

export default MillSummary;
